import {createApp, h} from 'vue';
import {Modal} from 'bootstrap';
import ConfirmModal from "../components/common/ConfirmModal.vue";

/**
 * Composable function to create and manage a confirmation modal
 * @param {Object} options - Configuration options for the modal
 * @param {String} options.title - Modal title
 * @param {String} options.confirmButtonText - Text for the confirm button
 * @param {String} options.cancelButtonText - Text for the cancel button
 * @param {Function} options.onConfirm - Callback function when confirm is clicked
 * @param {Function} options.onCancel - Callback function when cancel is clicked
 * @param {Function} options.renderContent - Function that returns the content to render in the modal body
 * @returns {Object} - Methods to control the modal
 */
export function useConfirmModal(options = {}) {
    let modalApp = null;
    let modalContainer = null;
    let bsModal = null;

    const createModalContainer = () => {
        modalContainer = document.createElement('div');
        modalContainer.id = 'confirm-modal-container';
        document.body.appendChild(modalContainer);
    };

    const cleanupModal = () => {
        if (bsModal) {
            bsModal.hide();
        }
        if (modalContainer && modalApp) {
            document.body.removeChild(modalContainer);
            modalApp.unmount();
            modalContainer = null;
            modalApp = null;
            bsModal = null;
        }
    };

    const showModal = () => {
        if (modalApp || modalContainer) {
            cleanupModal();
        }

        createModalContainer();

        let optionsComponent = {};
        for (let key in options) {
            if (typeof options[key] !== 'function') {
                optionsComponent[key] = options[key];
            }
        }

        modalApp = createApp({
            render() {
                return h(ConfirmModal, {
                    ...optionsComponent,
                    onConfirm: () => {
                        if (typeof options.onConfirm === 'function') {
                            options.onConfirm();
                        }
                        cleanupModal();
                    },
                    onCancel: () => {
                        if (typeof options.onCancel === 'function') {
                            options.onCancel();
                        }
                        cleanupModal();
                    },
                    onAnother: () => {
                        if (typeof options.onCancel === 'function') {
                            options.onAnother();
                        }
                        cleanupModal();
                    }
                }, {
                    default: () => options.renderContent ? options.renderContent() : null
                });
            }
        });

        modalApp.mount('#confirm-modal-container');

        // Instead of trying to call show() on the instance, we'll show the modal directly
        // by creating a Bootstrap Modal instance on the container
        bsModal = new Modal(document.getElementById('confirm-modal-container').querySelector('.modal'));
        bsModal.show();

        return {
            hide: () => {
                if (bsModal) {
                    bsModal.hide();
                }
            }
        };
    };

    return {
        showModal,
        cleanupModal
    };
}
